import React, { useState } from 'react'
import { Col, Badge, Modal, Button, Row } from 'react-bootstrap'
import { FaTimes } from 'react-icons/fa'
import CreatableSelect from 'react-select/creatable'

interface Props {
    allowedDomains: string[]
    disallowedDomainPaths: string[]
    onChange(domains: string[], disallowedDomainPaths: string[]): void
}

const DomainSettings: React.FC<Props> = ({ allowedDomains, disallowedDomainPaths, onChange }) => {
    const [showError, setShowError] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const stripCommonSubdomain = (hostname: string) => hostname.replace(/^www\d*\./i, '')

    const cleanDomain = (input: string) => {
        try {
            const url = input.startsWith('http') ? input : `https://${input}`
            const parsed = new URL(url)
            return stripCommonSubdomain(parsed.hostname.toLowerCase())
        } catch {
            return null
        }
    }

    const isValidDomain = (domain: string) => {
        return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)
    }

    const normalizePathPattern = (raw: string) => {
        let value = raw.trim()
        if (!value) return null

        value = value.split(/[?#]/)[0] || '/'
        if (!value.startsWith('/')) value = `/${value}`
        value = value.replace(/\/+/g, '/')

        if (value !== '/' && value.endsWith('/') && !value.endsWith('/*')) {
            value = value.slice(0, -1)
        }

        if (value.endsWith('/*')) {
            const prefix = value.slice(0, -2).replace(/\/+$/, '')
            value = `${prefix || ''}/*`
            if (!value.startsWith('/')) value = `/${value}`
        }

        if (!value.startsWith('/')) return null
        if (value.includes(' ')) return null

        return value
    }

    const normalizeDisallowedDomainPath = (raw: string) => {
        const value = raw.trim()
        if (!value) return null

        let parsed: URL
        try {
            parsed = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`)
        } catch {
            return null
        }

        const domain = stripCommonSubdomain(parsed.hostname.toLowerCase())
        if (!isValidDomain(domain)) return null

        const pathPattern = normalizePathPattern(parsed.pathname || '/')
        if (!pathPattern || pathPattern === '/') return null

        return `${domain}${pathPattern}`
    }

    const addDomain = (option: any) => {
        const value = option?.value?.trim()?.toLowerCase()
        if (!value) return

        const domain = cleanDomain(value)
        if (!domain || !isValidDomain(domain)) {
            setErrorMessage(`"${value}" is not a valid domain.`)
            setShowError(true)
            return
        }

        if (allowedDomains.includes(domain)) return

        onChange([...allowedDomains, domain], disallowedDomainPaths)
    }

    const addDisallowedDomainPath = (option: any) => {
        const value = option?.value?.trim()
        if (!value) return

        const normalized = normalizeDisallowedDomainPath(value)
        if (!normalized) {
            setErrorMessage(`"${value}" is not valid. Use a format like "example.com/checkout/*".`)
            setShowError(true)
            return
        }

        if (disallowedDomainPaths.includes(normalized)) return

        onChange(allowedDomains, [...disallowedDomainPaths, normalized])
    }

    const removeDomain = (domain: string) => onChange(
        allowedDomains.filter(d => d !== domain),
        disallowedDomainPaths.filter(rule => !rule.startsWith(`${domain}/`))
    )

    const removeDisallowedDomainPath = (rule: string) =>
        onChange(allowedDomains, disallowedDomainPaths.filter(r => r !== rule))

    return (
        <Col xs={12} className="mb-0">
            <Row>
                <Col xs={12} md={6} className="mb-3 mb-md-0">
                    <h5>Allowed Domains</h5>
                    <p className="text-muted mb-2">
                        Domains where your script is allowed to run.
                    </p>

                    <CreatableSelect
                        placeholder="Type domain (e.g. example.com)"
                        isClearable
                        formatCreateLabel={v => `Add "${v}"`}
                        onChange={addDomain}
                        onCreateOption={value => addDomain({ value })}
                    />

                    <div className="d-flex flex-wrap gap-2 mt-2">
                        {allowedDomains.map(domain => (
                            <Badge
                                key={domain}
                                bg="info"
                                className="d-flex align-items-center px-2 py-1"
                            >
                                {domain}
                                <FaTimes
                                    className="ms-2"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => removeDomain(domain)}
                                />
                            </Badge>
                        ))}
                    </div>
                </Col>

                <Col xs={12} md={6}>
                    <h5>Disallowed Domain Paths</h5>
                    <p className="text-muted mb-2">
                        Paths that should be blocked for allowed domains.
                    </p>

                    <CreatableSelect
                        placeholder="Type domain + path (e.g. example.com/checkout/*)"
                        isClearable
                        formatCreateLabel={v => `Add "${v}"`}
                        onChange={addDisallowedDomainPath}
                        onCreateOption={value => addDisallowedDomainPath({ value })}
                    />
                    <small className="text-muted d-block mt-1">
                        Use <code>*</code> at the end to block everything after that path. Example: <code>example.com/checkout/*</code> blocks <code>/checkout</code> and all subpaths.
                    </small>

                    <div className="d-flex flex-wrap gap-2 mt-2">
                        {disallowedDomainPaths.map(rule => (
                            <Badge
                                key={rule}
                                bg="danger"
                                className="d-flex align-items-center px-2 py-1"
                            >
                                {rule}
                                <FaTimes
                                    className="ms-2"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => removeDisallowedDomainPath(rule)}
                                />
                            </Badge>
                        ))}
                    </div>
                </Col>
            </Row>

            <Modal show={showError} onHide={() => setShowError(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Invalid Value</Modal.Title>
                </Modal.Header>
                <Modal.Body>{errorMessage}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowError(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Col>
    )
}

export default DomainSettings
